import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Loader from "shared/components/Loader";
import { PUBLIC_ROUTES } from "./routes.constants";
import { DAYS_EXPIRED_TOKEN, PermissionType } from "constants/common.constants";
import Setup from "views/pages/Setup";
import { useDispatch } from "react-redux";
import { useAuth } from "shared/hooks";
import { useGetUserInfoQuery } from "services/userService";
import {
  IS_VERIFIED_TYPE,
  REMEMBER_LOGIN,
  REMEMBER_VERIFY,
  USER_TOKEN_TYPE,
} from "constants/login.constants";
import { login, successVerify, updateAuth, updateProfile } from "modules/auth/authSlice";
import { ProfileUser } from "shared/models/profile.model";
import { useCheckSetupModeMutation } from "services/authServiceWithToken";
import { cookiesStorage } from "shared/helpers/cookiesStorage";
import RequireAuth from "shared/components/RequiredAuth";
import { SUPER_ADMIN, ADMIN, MERCHANT, PARTNER } from "constants/userManagement.constants";
import {
  updateSessionUser,
  updateSesstionUserFromSessionStorage,
} from "modules/sessionUser/sessionUserSlice";
import useSessionUser from "shared/hooks/useSessionUser";
import { useGetTransactionsQuery } from "services/reportService";

const Admin = lazy(() => import("views/containers/Admin"));
const UnAuthenticate = lazy(() => import("views/containers/UnAuthenticate"));

export const publicRoutes = [
  {
    path: PUBLIC_ROUTES.BLANK,
    component: lazy(() => import("views/pages/Login")),
  },
  {
    path: PUBLIC_ROUTES.NOT_FOUND,
    component: lazy(() => import("views/pages/NotFound")),
  },
  {
    path: PUBLIC_ROUTES.FOR_BIDDEN,
    component: lazy(() => import("views/pages/Forbidden")),
  },
  {
    path: PUBLIC_ROUTES.NOT_FOUND,
    component: lazy(() => import("views/pages/Login")),
  },
  {
    path: PUBLIC_ROUTES.FORGOT_PASSWORD,
    component: lazy(() => import("views/pages/ForgotPassword")),
  },
  {
    path: PUBLIC_ROUTES.MAINTENANCE,
    component: lazy(() => import("views/pages/Maintenance")),
  },
];

export const adminRoutes = [
  {
    path: "",
    component: lazy(() => import("views/pages/admin/Dashboard")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/dashboard",
    component: lazy(() => import("views/pages/admin/Dashboard")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/partner-setting",
    component: lazy(() => import("views/pages/admin/PartnerSetting")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/onboarding",
    component: lazy(() => import("views/pages/admin/Onboarding")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/onboarding-management",
    component: lazy(() => import("views/pages/admin/Onboarding")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/user-management",
    component: lazy(() => import("views/pages/admin/UserManagement")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/merchant-management",
    component: lazy(() => import("views/pages/admin/MerchantManagement")),
    permission: PermissionType.merchantSettings,
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/disbursement-management",
    component: lazy(() => import("views/pages/admin/Disbursement")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/new-payout",
    component: lazy(() => import("views/pages/admin/NewPayout")),
    permission: PermissionType.reportDisbursements,
    roles: [SUPER_ADMIN, ADMIN, MERCHANT, PARTNER],
  },
  {
    path: "/payout-management",
    component: lazy(() => import("views/pages/admin/Payout")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/psp-management",
    component: lazy(() => import("views/pages/admin/PSPManagement")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/partner-management",
    component: lazy(() => import("views/pages/admin/PartnerManagement")),
    permission: PermissionType.adminPartners,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/profile",
    component: lazy(() => import("views/pages/admin/Profile")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/transactions",
    component: lazy(() => import("views/pages/admin/Transaction")),
    permission: PermissionType.reportTransactions,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/new-transactions",
    component: lazy(() => import("views/pages/admin/NewTransaction")),
    permission: PermissionType.reportTransactions,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/payouts",
    component: lazy(() => import("views/pages/admin/Disbursement")),
    permission: PermissionType.reportDisbursements,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/virtual-terminal",
    component: lazy(() => import("views/pages/admin/VirtualTerminal")),
    permission: PermissionType.virtualTerminal,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/pay-by-link-history",
    component: lazy(() => import("views/pages/admin/UrlManagement")),
    permission: PermissionType.urlManagement,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/virtual-terminal/refund-request",
    component: lazy(() => import("views/pages/admin/VirtualTerminal/RefundRequest")),
    permission: PermissionType.virtualTerminal,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/merchant-setting",
    component: lazy(() => import("views/pages/admin/MerchantSetting")),
    permission: PermissionType.merchantSettings,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  // {
  //   path: "/customer-management",
  //   component: lazy(() => import("views/pages/admin/CustomerManagement")),
  //   permission: PermissionType.customerManagement,
  //   roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  // },
  {
    path: "/settings",
    component: lazy(() => import("views/pages/admin/NotificationCenter")),
    permission: PermissionType.merchantNotifications,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/audit-log",
    component: lazy(() => import("views/pages/admin/AuditLog")),
    permission: PermissionType.merchantAuditLogs,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/wallet-internal-transfer",
    component: lazy(() => import("views/pages/admin/WalletInternalTransfer")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN],
  },
  {
    path: "/receivables-management",
    component: lazy(() => import("views/pages/admin/Receivable")),
    permission: PermissionType.receivable,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/notification-settings",
    component: lazy(() => import("views/pages/admin/NotificationSettings")),
    permission: PermissionType.merchantNotifications,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/auto-charge",
    component: lazy(() => import("views/pages/admin/AutoCharge")),
    permission: PermissionType.reportAutoCharge,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/chargeback",
    component: lazy(() => import("views/pages/admin/Chargeback")),
    permission: PermissionType.chargebackReport,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/chargeback-management",
    component: lazy(() => import("views/pages/admin/Chargeback")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/communication-setting",
    component: lazy(() => import("views/pages/admin/CommunicationSetting")),
    permission: PermissionType.communicationSettings,
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/onboarding-settings",
    component: lazy(() => import("views/pages/admin/OnboardingSettings")),
    permission: PermissionType.merchantSettings,
    roles: [SUPER_ADMIN, ADMIN, PARTNER, MERCHANT],
  },
  {
    path: "/cross-check",
    component: lazy(() => import("views/pages/admin/CrossCheck")),
    permission: PermissionType.adminCrossCheck,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/admin-payment",
    component: lazy(() => import("views/pages/admin/AdminPayment")),
    permission: PermissionType.adminUsers,
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/vault-management",
    component: lazy(() => import("views/pages/admin/VaultService")),
    permission: PermissionType.vaultService,
    roles: [SUPER_ADMIN, ADMIN, MERCHANT],
  },
  {
    path: "/vault-integration",
    component: lazy(() => import("views/pages/admin/VaultService/Integration")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/payment-setting",
    component: lazy(() => import("views/pages/admin/PaymentSetting")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN, PARTNER],
  },
  {
    path: "/refund-request-management",
    component: lazy(() => import("views/pages/admin/RefundRequestManagement")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN],
  },
  {
    path: "/adjustment-management",
    component: lazy(() => import("views/pages/admin/AdjustmentRequest")),
    permission: "",
    roles: [SUPER_ADMIN, ADMIN],
  },
];

function ManageRoutes() {
  const dispatch = useDispatch();
  const { token, verified } = useAuth();
  const { transaction, dashboard, ...otherSessionUser } = useSessionUser();

  //API
  const {
    data: profile,
    isSuccess,
    isLoading: isLoadingInfo,
    isFetching,
    refetch,
  } = useGetUserInfoQuery(undefined, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });
  const [checkSetupMode, { isLoading }] = useCheckSetupModeMutation();

  useEffect(() => {
    handleCheckAuth();
  }, []);

  useEffect(() => {
    if (token) {
      refetch();
    }
  }, [token]);

  useEffect(() => {
    if (isSuccess) {
      const data = profile?.data;
      const newData: ProfileUser = {
        ...data,
        permissions: [...data.permissions.map((item: any) => item.name)],
      };
      if (data.roles.length > 0) {
        newData.roles = data.roles[0]?.name;
        newData.roleId = data.roles[0]?.id;
        newData.permissions = [
          ...newData.permissions,
          ...data.roles[0].permissions.map((item: any) => item.name),
        ];
        if (newData.roles !== MERCHANT) {
          dispatch(
            updateSessionUser({
              ...otherSessionUser,
              dashboard: {
                ...dashboard,
                showColumns: !transaction.showColumns.includes("merchant_name")
                  ? [...transaction.showColumns, "merchant_name"]
                  : [...transaction.showColumns],
              },
              transaction: {
                ...transaction,
                showColumns: !transaction.showColumns.includes("merchant_name")
                  ? [...transaction.showColumns, "merchant_name"]
                  : [...transaction.showColumns],
              },
            })
          );
        } else {
          dispatch(
            updateSessionUser({
              ...otherSessionUser,
              dashboard: {
                ...dashboard,
                showColumns: !transaction.showColumns.includes("guest_name")
                  ? [...transaction.showColumns, "guest_name"]
                  : [...transaction.showColumns],
              },
              transaction: {
                ...transaction,
                showColumns: !transaction.showColumns.includes("guest_name")
                  ? [...transaction.showColumns, "guest_name"]
                  : [...transaction.showColumns],
              },
            })
          );
        }

        cookiesStorage.set("permissions", JSON.stringify(newData.permissions), DAYS_EXPIRED_TOKEN);
      }

      dispatch(updateProfile(newData));
    }
  }, [isLoadingInfo, isFetching]);

  const handleCheckAuth = async () => {
    dispatch(updateSesstionUserFromSessionStorage());
    if (!window.location.href.includes("iframe")) {
      const responseSetupMode = await checkSetupMode({}).unwrap();
      if (responseSetupMode) {
        if (!responseSetupMode?.data?.is_changed_password) {
          cookiesStorage.remove(REMEMBER_LOGIN);
          cookiesStorage.remove(USER_TOKEN_TYPE);
        }
        if (
          !responseSetupMode?.data?.is_setup_email_authenticate &&
          !responseSetupMode?.data?.is_setup_ga_authenticate
        ) {
          cookiesStorage.remove(REMEMBER_VERIFY);
        }
        dispatch(
          updateAuth({
            isVerify2faEmail: responseSetupMode?.data?.is_setup_email_authenticate,
            isVerify2faGa: responseSetupMode?.data?.is_setup_ga_authenticate,
            isVerifyPassword: responseSetupMode?.data?.is_changed_password,
          })
        );

        if (
          cookiesStorage.get(REMEMBER_LOGIN) &&
          cookiesStorage.get(REMEMBER_VERIFY) &&
          responseSetupMode?.data?.is_changed_password &&
          (responseSetupMode?.data?.is_setup_email_authenticate ||
            responseSetupMode?.data?.is_setup_ga_authenticate)
        ) {
          dispatch(successVerify());
        }
      }
    }

    if (cookiesStorage.get(IS_VERIFIED_TYPE)) {
      dispatch(successVerify());
    }
    if (cookiesStorage.get(USER_TOKEN_TYPE)) {
      dispatch(
        login({
          token: String(cookiesStorage.get(USER_TOKEN_TYPE)),
          password: "",
        })
      );
    }
    if (cookiesStorage.get(USER_TOKEN_TYPE)) {
      dispatch(
        login({
          token: cookiesStorage.get(USER_TOKEN_TYPE)
            ? String(cookiesStorage.get(USER_TOKEN_TYPE))
            : "",
          password: "",
        })
      );
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Suspense fallback={<Loader />}>
          <BrowserRouter
            basename={process.env.REACT_APP_SUB_PATH ? `${process.env.REACT_APP_SUB_PATH}` : ""}
          >
            <Routes>
              <Route path={"/admin/*"} element={<Admin />} />
              <Route
                path={"/setup/*"}
                element={
                  <RequireAuth>
                    <Setup />
                  </RequireAuth>
                }
              />
              <Route path="/*" element={<UnAuthenticate />} />
              <Route path="*" element={<Navigate to={PUBLIC_ROUTES.NOT_FOUND} replace />} />
            </Routes>
          </BrowserRouter>
        </Suspense>
      )}
    </>
  );
}

export default ManageRoutes;
